export interface AudioMixer {
  readonly audioContext: AudioContext;
  readonly masterDestination: MediaStreamAudioDestinationNode;
  micSource: MediaStreamAudioSourceNode | null;
  centralAudioSource: MediaElementAudioSourceNode | null;
}

export function createAudioMixer(micStream: MediaStream | null): AudioMixer {
  const audioContext = new AudioContext();
  const masterDestination = audioContext.createMediaStreamDestination();

  let micSource: MediaStreamAudioSourceNode | null = null;

  if (micStream !== null) {
    micSource = audioContext.createMediaStreamSource(micStream);
    micSource.connect(masterDestination);
  }

  return {
    audioContext,
    masterDestination,
    micSource,
    centralAudioSource: null,
  };
}

/**
 * `createMediaElementSource` may only be called once per `<audio>` element for
 * its lifetime — a second call throws, which is why a mixer already holding a
 * source is a no-op rather than a re-attach.
 */
export function connectCentralAudioToMixer(
  mixer: AudioMixer,
  centralAudioElement: HTMLAudioElement
): void {
  if (mixer.centralAudioSource !== null) {
    return;
  }

  try {
    const audioSource =
      mixer.audioContext.createMediaElementSource(centralAudioElement);

    audioSource.connect(mixer.masterDestination);
    audioSource.connect(mixer.audioContext.destination);

    mixer.centralAudioSource = audioSource;
  } catch {
    return;
  }
}

function disconnectSilently(node: { disconnect: () => void } | null): void {
  try {
    node?.disconnect();
  } catch {
    return;
  }
}

export function cleanupAudioMixer(mixer: AudioMixer): void {
  disconnectSilently(mixer.micSource);
  disconnectSilently(mixer.centralAudioSource);

  void mixer.audioContext.close().catch(() => undefined);
}
